import React from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import useBackendHealth from "../hooks/useBackendHealth";

/**
 * SystemHealthBanner — Thin status bar that appears only when something needs attention:
 *   - Backend offline (red)
 *   - Syncing pending ops (amber)
 *   - Recently recovered with pending ops still being processed
 * Hidden when status is online and queue is empty.
 */
export default function SystemHealthBanner() {
  const { status, pendingCount, flushing } = useBackendHealth();

  // Nothing to show
  if (status === "online" && pendingCount === 0 && !flushing) return null;
  if (status === "checking" && pendingCount === 0) return null;

  let bg = "bg-amber-500";
  let icon = <Loader2 className="w-4 h-4 animate-spin" />;
  let message = "Sincronizando...";
  let testId = "system-health-banner-syncing";

  if (status === "offline") {
    bg = "bg-red-600";
    icon = <AlertTriangle className="w-4 h-4" />;
    message = pendingCount > 0
      ? `Sin conexión — ${pendingCount} ${pendingCount === 1 ? "registro guardado" : "registros guardados"} localmente. Se sincronizarán automáticamente.`
      : "Sin conexión con el servidor. Tus datos se guardarán localmente si registras algo.";
    testId = "system-health-banner-offline";
  } else if (flushing) {
    bg = "bg-amber-500";
    icon = <Loader2 className="w-4 h-4 animate-spin" />;
    message = `Sincronizando ${pendingCount} ${pendingCount === 1 ? "registro pendiente" : "registros pendientes"}...`;
    testId = "system-health-banner-syncing";
  } else if (pendingCount > 0) {
    bg = "bg-amber-500";
    icon = <AlertTriangle className="w-4 h-4" />;
    message = `${pendingCount} ${pendingCount === 1 ? "registro pendiente" : "registros pendientes"} de sincronizar`;
    testId = "system-health-banner-pending";
  } else if (status === "online") {
    bg = "bg-emerald-600";
    icon = <CheckCircle2 className="w-4 h-4" />;
    message = "Conexión restablecida";
    testId = "system-health-banner-online";
  }

  return (
    <div
      className={`${bg} text-white text-sm px-4 py-2 flex items-center justify-center gap-2 sticky top-0 z-[60] shadow-md`}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      {icon}
      <span className="font-medium">{message}</span>
    </div>
  );
}
